import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import { DataTable, type Column } from '@/components/shared/DataTable';
import { FilterBar, type FilterOption } from '@/components/shared/FilterBar';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import {
  inventorySimulationService,
  type CoverageGroupRow,
  type CoverageProductRow,
  type CoverageStatus,
  type SimulationType,
} from '@/services/inventorySimulationService';
import { CoverageKpisCards } from '../components/CoverageKpis';
import { SimulationParamsPanel } from '../components/SimulationParamsPanel';
import { StockProjectionTable } from '../components/StockProjectionTable';
import {
  useCoverageProducts,
  useInventorySimulationFilters,
  useStockProjection,
} from '../hooks/useInventorySimulation';
import { formatQty, type Granularity } from '../utils/projection';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';

const EXPORT_PAGE_SIZE = 500;
const EXPORT_MAX_ROWS = 10000;

interface FilterState {
  supplier_id?: string;
  family_id?: string;
  product_id?: string;
  coverage_status?: CoverageStatus;
}

function coverageBadge(status: CoverageStatus, t: (k: string) => string) {
  const map: Record<CoverageStatus, string> = {
    OK: 'bg-emerald-100 text-emerald-800',
    BELOW_SAFETY: 'bg-amber-100 text-amber-800',
    STOCKOUT: 'bg-red-100 text-red-800',
  };
  const label: Record<CoverageStatus, string> = {
    OK: t('inventorySimulation.coverage.ok'),
    BELOW_SAFETY: t('inventorySimulation.coverage.belowSafety'),
    STOCKOUT: t('inventorySimulation.coverage.stockout'),
  };
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-[11px] font-medium ${map[status]}`}>
      {label[status]}
    </span>
  );
}

export function InventorySimulationPage() {
  const { t } = useTranslation();
  const warehouseId = useWarehouseId();

  const [filterState, setFilterState] = useState<FilterState>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [selectedSupplierByGroup, setSelectedSupplierByGroup] = useState<Record<string, number>>({});
  const [simulationType, setSimulationType] = useState<SimulationType>('WITH_SUGGESTED_ORDER');
  const [granularity, setGranularity] = useState<Granularity>('week');
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    setPage(1);
  }, [filterState, warehouseId]);

  const filtersQuery = useInventorySimulationFilters(warehouseId);

  const productsParams = useMemo(
    () => ({
      warehouse_id: warehouseId ?? undefined,
      supplier_id: filterState.supplier_id,
      family_id: filterState.family_id,
      product_id: filterState.product_id,
      coverage_status: filterState.coverage_status,
      sort: sortField ?? undefined,
      direction: sortField ? sortDirection : undefined,
      page,
      page_size: pageSize,
    }),
    [warehouseId, filterState, sortField, sortDirection, page, pageSize],
  );

  const handleSort = (accessor: string) => {
    setPage(1);
    if (sortField === accessor) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(accessor);
      setSortDirection('asc');
    }
  };

  const productsQuery = useCoverageProducts(productsParams);
  const projectionQuery = useStockProjection(selectedRowId);

  const products = productsQuery.data;
  const groups = products?.results ?? [];
  const kpis = products?.coverage_kpis ?? { covered: 0, below_safety: 0, stockout: 0, total: 0 };
  const pagination = products?.pagination;

  const pickSupplier = (group: CoverageGroupRow): CoverageProductRow => {
    const chosenId = selectedSupplierByGroup[group.group_key] ?? group.default_product_result_id;
    return group.suppliers.find((s) => s.product_result_id === chosenId) ?? group.suppliers[0];
  };

  type DisplayRow = CoverageProductRow & { group_key: string; suppliers: CoverageProductRow[] };

  const displayRows: DisplayRow[] = useMemo(
    () =>
      groups
        .map((group) => {
          const supplier = pickSupplier(group);
          if (!supplier) return null;
          return { ...supplier, group_key: group.group_key, suppliers: group.suppliers };
        })
        .filter((row): row is DisplayRow => row !== null),
    [groups, selectedSupplierByGroup],
  );

  const handleSupplierChange = (row: DisplayRow, newId: number) => {
    setSelectedSupplierByGroup((prev) => ({ ...prev, [row.group_key]: newId }));
    const belongsToOpenGroup = row.suppliers.some((s) => s.product_result_id === selectedRowId);
    if (belongsToOpenGroup) setSelectedRowId(newId);
  };

  const filterOptions: FilterOption[] = useMemo(() => {
    const data = filtersQuery.data;
    if (!data) return [];
    return [
      {
        id: 'product_id',
        name: t('inventorySimulation.filters.product'),
        options: data.products.map((p) => ({ value: p.id, label: `${p.code} - ${p.name}` })),
        enableSelectAll: false,
      },
      {
        id: 'supplier_id',
        name: t('inventorySimulation.filters.supplier'),
        options: data.suppliers.map((s) => ({ value: s.id, label: s.name })),
      },
      {
        id: 'family_id',
        name: t('inventorySimulation.filters.family'),
        options: data.families.map((f) => ({ value: f.id, label: f.name })),
      },
      {
        id: 'coverage_status',
        name: t('inventorySimulation.filters.coverage'),
        options: data.coverage_statuses.map((c) => ({ value: c.id, label: c.name })),
      },
    ];
  }, [filtersQuery.data, t]);

  const activeFilters = useMemo(() => {
    const entries: Array<{ id: string; label: string; value: string }> = [];
    const data = filtersQuery.data;
    if (filterState.supplier_id && data) {
      const found = data.suppliers.find((s) => String(s.id) === filterState.supplier_id);
      entries.push({ id: 'supplier_id', label: found?.name ?? filterState.supplier_id, value: filterState.supplier_id });
    }
    if (filterState.family_id && data) {
      const found = data.families.find((f) => String(f.id) === filterState.family_id);
      entries.push({ id: 'family_id', label: found?.name ?? filterState.family_id, value: filterState.family_id });
    }
    if (filterState.product_id && data) {
      const found = data.products.find((p) => String(p.id) === filterState.product_id);
      entries.push({
        id: 'product_id',
        label: found ? `${found.code} - ${found.name}` : filterState.product_id,
        value: filterState.product_id,
      });
    }
    if (filterState.coverage_status && data) {
      const found = data.coverage_statuses.find((c) => c.id === filterState.coverage_status);
      entries.push({
        id: 'coverage_status',
        label: found?.name ?? filterState.coverage_status,
        value: filterState.coverage_status,
      });
    }
    return entries;
  }, [filterState, filtersQuery.data]);

  const handleFilterChange = (changed: Record<string, string[]>) => {
    setFilterState((prev) => {
      const next = { ...prev };
      Object.entries(changed).forEach(([key, values]) => {
        const value = values[0];
        if (!value) delete (next as Record<string, string | undefined>)[key];
        else (next as Record<string, string>)[key] = value;
      });
      return next;
    });
  };

  const handleClearFilter = (filterId: string) => {
    setFilterState((prev) => {
      const next = { ...prev };
      delete (next as Record<string, string | undefined>)[filterId];
      return next;
    });
  };

  const columns: Column[] = [
    {
      header: t('inventorySimulation.table.code'),
      accessor: 'product_code',
      sortable: true,
      width: '110px',
    },
    {
      header: t('inventorySimulation.table.product'),
      accessor: 'product_name',
      sortable: true,
    },
    {
      header: t('inventorySimulation.table.warehouse'),
      accessor: 'warehouse_code',
      width: '110px',
    },
    {
      header: t('inventorySimulation.table.supplier'),
      accessor: 'supplier_name',
      width: '200px',
      cell: (_value, item: DisplayRow) => {
        if (item.suppliers.length <= 1) {
          return <span className="block truncate">{item.supplier_name ?? '—'}</span>;
        }
        return (
          <select
            value={item.product_result_id}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleSupplierChange(item, Number(e.target.value))}
            className="w-full max-w-[190px] rounded border border-gray-300 bg-white px-1.5 py-1 text-xs focus:border-[#1681ba] focus:outline-none"
          >
            {item.suppliers.map((s) => (
              <option key={s.product_result_id} value={s.product_result_id}>
                {s.supplier_name ?? '—'}
                {s.is_primary ? ' *' : ''}
              </option>
            ))}
          </select>
        );
      },
      exportValue: (_value, item: DisplayRow) => item.supplier_name ?? '',
    },
    {
      header: t('inventorySimulation.table.coverage'),
      accessor: 'coverage_status',
      width: '120px',
      cell: (value: CoverageStatus) => coverageBadge(value, t),
      exportValue: (value: CoverageStatus) => value,
    },
    {
      header: t('inventorySimulation.table.suggested'),
      accessor: 'final_suggested_quantity',
      align: 'right',
      width: '110px',
      sortable: true,
      cell: (value) => formatQty(value),
      exportType: 'number',
    },
    {
      header: t('inventorySimulation.table.firstStockout'),
      accessor: 'first_stockout_date',
      width: '120px',
      cell: (value: string | null) => value ?? '—',
      exportType: 'date',
    },
  ];

  const handleExport = async (): Promise<CoverageProductRow[]> => {
    const all: CoverageProductRow[] = [];
    let exportPage = 1;
    for (;;) {
      const response = await inventorySimulationService.getProducts({
        ...productsParams,
        page: exportPage,
        page_size: EXPORT_PAGE_SIZE,
      });
      for (const group of response.results) {
        const chosenId = selectedSupplierByGroup[group.group_key] ?? group.default_product_result_id;
        const supplier =
          group.suppliers.find((s) => s.product_result_id === chosenId) ?? group.suppliers[0];
        if (supplier) all.push(supplier);
      }
      if (!response.pagination.has_next || all.length >= EXPORT_MAX_ROWS) break;
      exportPage += 1;
    }
    return all;
  };

  const hasRun = filtersQuery.data ? filtersQuery.data.run !== null : true;
  const runMeta = products?.run ?? filtersQuery.data?.run ?? null;

  const protectionOptions = useMemo(
    () => ({
      calculationDate: projectionQuery.data?.run?.calculation_date ?? null,
      protectionPeriodDays: projectionQuery.data?.context?.protection_period_days ?? null,
      leadTimeDays: projectionQuery.data?.context?.lead_time_days ?? null,
      reviewPeriodDays: projectionQuery.data?.context?.review_period_days ?? null,
    }),
    [projectionQuery.data?.run?.calculation_date, projectionQuery.data?.context],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs text-gray-500">
            {runMeta ? (
              <>
                {t('inventorySimulation.runDate')}: <span className="font-medium text-gray-700">{runMeta.calculation_date}</span>
              </>
            ) : (
              t('inventorySimulation.noRun')
            )}
          </p>
        </div>
      </div>

      <CoverageKpisCards kpis={kpis} loading={productsQuery.isLoading} />

      <FilterBar
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        activeFilters={activeFilters}
        onClearFilter={handleClearFilter}
        onClearAllFilters={() => setFilterState({})}
      />

      {!hasRun ? (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600">
          <Info className="h-4 w-4 text-gray-400" />
          {t('inventorySimulation.noRunDetail')}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={displayRows}
          compact
          loading={productsQuery.isFetching}
          onRowClick={(item: DisplayRow) => setSelectedRowId(item.product_result_id)}
          rowClassName={(item: DisplayRow) =>
            item.suppliers.some((s) => s.product_result_id === selectedRowId) ? 'bg-blue-50' : ''
          }
          allowExport
          exportFilename="simulacion-inventario-cobertura"
          onExport={handleExport}
          externalPagination={
            pagination
              ? {
                  currentPage: pagination.current_page,
                  totalPages: pagination.total_pages,
                  totalCount: pagination.total_count,
                  pageSize: pagination.page_size,
                  onPageChange: setPage,
                  onPageSizeChange: (size) => {
                    setPageSize(size);
                    setPage(1);
                  },
                }
              : undefined
          }
          externalSorting={{ field: sortField, direction: sortDirection, onSort: handleSort }}
        />
      )}

      {selectedRowId != null && projectionQuery.data?.context ? (
        <div className="space-y-4">
          <SimulationParamsPanel context={projectionQuery.data.context} />
          <StockProjectionTable
            projections={projectionQuery.data.projections}
            simulationType={simulationType}
            granularity={granularity}
            protectionOptions={protectionOptions}
            onSimulationTypeChange={setSimulationType}
            onGranularityChange={setGranularity}
            loading={projectionQuery.isFetching}
            exportFilename={`simulacion-${projectionQuery.data.context.product_code_snapshot}`}
          />
        </div>
      ) : selectedRowId != null && projectionQuery.isFetching ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
          {t('common.loading')}
        </div>
      ) : null}
    </div>
  );
}
